import { Maybe, RemoteData } from '@passionware/monads';
import {
  CustomFieldType,
  CustomFieldDefinition,
  CustomFieldGroupDefinition,
  MetastructureFieldCore,
  MetastructureFieldCustom,
  MetastructureFieldCustomGroup,
  MetastructureFieldType
} from '__types__';
import type { AppScopeIdentity } from 'v5/core/app-scope/appScope.types';

export interface FieldNameTypeMapping {
  [fieldName: string]: CustomFieldType; // e.g., { "custom_1": CustomFieldType.SINGLE_SELECT, "custom_2": CustomFieldType.SHORT_TEXT }
}

export type MetastructureRequest = {
  type: 'resource' | 'production' | 'booking';
  subtypeId: number;
  scope: AppScopeIdentity;
};

export type MetastructureAggregateRequest = {
  type: 'resource' | 'production' | 'booking';
  scope: AppScopeIdentity;
};

export type ResolvedMetastructureFieldCustom = MetastructureFieldCustom & {
  type: MetastructureFieldType.Custom;
  customFieldDefinition: CustomFieldDefinition;
};

export type ResolvedMetastructureFieldCustomGroup =
  MetastructureFieldCustomGroup & {
    type: MetastructureFieldType.CustomGroup;
    customFieldGroupDefinition: CustomFieldGroupDefinition;
  };

export type ResolvedMetastructureFieldCore = MetastructureFieldCore & {
  type: MetastructureFieldType.Core;
};

export type ResolvedMetastructureField =
  | ResolvedMetastructureFieldCore
  | ResolvedMetastructureFieldCustom
  | ResolvedMetastructureFieldCustomGroup;

export const resolvedMetastructureFieldUtils = {
  isCustomField: (
    field: ResolvedMetastructureField
  ): field is ResolvedMetastructureFieldCustom =>
    field.type === MetastructureFieldType.Custom,
  isCustomFieldGroup: (
    field: ResolvedMetastructureField
  ): field is ResolvedMetastructureFieldCustomGroup =>
    field.type === MetastructureFieldType.CustomGroup,
  isCoreField: (
    field: ResolvedMetastructureField
  ): field is ResolvedMetastructureFieldCore =>
    field.type === MetastructureFieldType.Core
};

export interface MetastructureMappingService {
  getAllFieldNameTypeMappings(): Promise<FieldNameTypeMapping>;
  useCustomFieldDefinitions(): RemoteData<Record<
    number,
    CustomFieldDefinition
  > | null>;
  ensureCustomFieldDefinitions(): Promise<
    Record<number, CustomFieldDefinition>
  >;
  useMetastructure(
    request: Maybe<MetastructureRequest>
  ): RemoteData<ResolvedMetastructureField[]>;
  useMetastructures(
    requests: MetastructureRequest[]
  ): RemoteData<ResolvedMetastructureField[]>[];
  useMetastructureAggregateRequests(
    request: Maybe<MetastructureAggregateRequest>
  ): MetastructureRequest[];
  ensureMetastructure(
    request: MetastructureRequest
  ): Promise<ResolvedMetastructureField[]>;
}

export interface WithMetastructureMappingService {
  metastructureMappingService: MetastructureMappingService;
}
